import { fp16ToFp32, fp32ToFp16, uint8BytesToUint16 } from './fp16';

function clampInt(v: number, lo: number, hi: number): number {
  if (v < lo) return lo;
  if (v > hi) return hi;

  return v;
}

export type PatchForImagePointArgs = {
  x: number;
  y: number;
  imgW: number;
  imgH: number;
  inputW: number;
  inputH: number;
  padLeft: number;
  padTop: number;
};

// Row-major view over an L2-normalized DinoV2 patch grid stored as fp16.
//
// The on-the-wire format from vision-bot is `[gridH, gridW, dim]` little-endian
// fp16, packed into a flat byte buffer. We hold those bytes as a Uint16Array
// (since Float16Array isn't standard on the runtimes we support) and convert
// to fp32 on read via fp16ToFp32.
export class PatchTensor {
  public constructor(
    public readonly gridH: number,
    public readonly gridW: number,
    public readonly dim: number,
    private readonly buf: Uint16Array,
  ) {
    if (buf.length !== gridH * gridW * dim) {
      throw new Error(
        `PatchTensor buffer length ${buf.length} does not match ` +
          `gridH*gridW*dim = ${gridH}*${gridW}*${dim} = ${gridH * gridW * dim}`,
      );
    }
  }

  public patchAt(row: number, col: number): Float32Array {
    if (row < 0 || row >= this.gridH || col < 0 || col >= this.gridW) {
      throw new Error(
        `patchAt(${row}, ${col}) out of bounds for ${this.gridH}x${this.gridW}`,
      );
    }

    const start = (row * this.gridW + col) * this.dim;
    const out = new Float32Array(this.dim);

    for (let i = 0; i < this.dim; i += 1) {
      out[i] = fp16ToFp32(this.buf[start + i]);
    }

    return out;
  }

  // Decode a single patch into the caller-provided buffer. The heatmap pass
  // re-uses a shared scratch buffer to avoid per-patch allocations.
  // eslint-disable-next-line no-param-reassign
  public fillPatch(row: number, col: number, out: Float32Array): void {
    const start = (row * this.gridW + col) * this.dim;

    for (let i = 0; i < this.dim; i += 1) {
      // eslint-disable-next-line no-param-reassign
      out[i] = fp16ToFp32(this.buf[start + i]);
    }
  }

  // Map an image pixel (in original image coordinates) to the patch row/col
  // that would have received it after the same letterbox-resize the encoder
  // applied. Clicks landing in the letterbox padding clamp to the nearest
  // edge patch — the user can't pick "padding" as a reference.
  public patchForImagePoint(args: PatchForImagePointArgs): {
    row: number;
    col: number;
  } {
    const { x, y, imgW, imgH, inputW, inputH, padLeft, padTop } = args;

    const contentW = inputW - 2 * padLeft;
    const contentH = inputH - 2 * padTop;

    if (contentW <= 0 || contentH <= 0) {
      throw new Error(
        `patchForImagePoint: invalid letterbox content dims ${contentW}x${contentH}`,
      );
    }

    // Original-image px → letterboxed-input px.
    const xInput = (x * contentW) / imgW + padLeft;
    const yInput = (y * contentH) / imgH + padTop;

    const patchPxW = inputW / this.gridW;
    const patchPxH = inputH / this.gridH;

    const col = clampInt(Math.floor(xInput / patchPxW), 0, this.gridW - 1);
    const row = clampInt(Math.floor(yInput / patchPxH), 0, this.gridH - 1);

    return { row, col };
  }

  // Construct a PatchTensor from a Float32Array, quantizing through fp16 to
  // match the wire-format storage shape. Useful in tests and offline tooling
  // where raw fp32 data is more convenient to construct than byte buffers.
  public static fromFloat32(
    gridH: number,
    gridW: number,
    dim: number,
    values: Float32Array,
  ): PatchTensor {
    if (values.length !== gridH * gridW * dim) {
      throw new Error(
        `PatchTensor.fromFloat32: length mismatch (${values.length} vs ${gridH * gridW * dim})`,
      );
    }

    const buf = new Uint16Array(values.length);

    for (let i = 0; i < values.length; i += 1) {
      buf[i] = fp32ToFp16(values[i]);
    }

    return new PatchTensor(gridH, gridW, dim, buf);
  }

  // Return the underlying fp16 buffer as a Uint8Array (little-endian, matching
  // the on-the-wire format produced by vision-bot). Writes via DataView to
  // force LE byte order — `Uint16Array.buffer` is in platform-native order,
  // which would mismatch the LE read path in `uint8BytesToUint16` on
  // big-endian hosts.
  public toBytes(): Uint8Array {
    const out = new Uint8Array(this.buf.length * 2);
    const view = new DataView(out.buffer);

    for (let i = 0; i < this.buf.length; i += 1) {
      view.setUint16(i * 2, this.buf[i], true);
    }

    return out;
  }

  public static fromTensorBytes(
    gridH: number,
    gridW: number,
    embeddingDim: number,
    values: Uint8Array,
  ): PatchTensor {
    const expectedBytes = gridH * gridW * embeddingDim * 2;

    if (values.byteLength !== expectedBytes) {
      throw new Error(
        `PatchTensor.fromTensorBytes: expected ${expectedBytes} bytes ` +
          `(${gridH}*${gridW}*${embeddingDim}*2), got ${values.byteLength}`,
      );
    }

    return new PatchTensor(
      gridH,
      gridW,
      embeddingDim,
      uint8BytesToUint16(values),
    );
  }
}
